import { DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, effect, inject, OnDestroy, signal, untracked } from '@angular/core';
import { CreateManualModerationDto, CreatorModerationStatusEnum, CreatorResponseDto, CreatorStatusFilterButton, FilterCreatorsDto, ModerationFilterStatsDto } from '../../../../common/general-types/creators.dto';
import { AdminFilter } from '../../services/admin-filter/admin-filter';
import { catchError, debounceTime, distinctUntilChanged, finalize, Observable, of, Subject, Subscription } from 'rxjs';
import { CreatorService } from '../../../../services/creator/creator.service';
import { ModerateService } from '../../../../services/moderate/moderate';
import { ImageService } from '../../../../services/image/image.service';
import { ShortNumberPipe } from '../../../../pipes/short-number/short-number.pipe';
import { FormatService } from '../../../../services/format/format.service';
import { TooltipDirective } from '../../../../common/directives/tooltip/tooltip.directive';
import { InfiniteScrollDirective } from 'ngx-infinite-scroll';
import { NotificationType } from '@/app/common/app-config/filter-models/misc';
import { ToastNotificationService } from '@/app/atoms/toast-notification/toast-notification.service';

interface CreatorsFilterResponse {
  count: number;
  data: CreatorResponseDto[];
}

@Component({
  selector: 'admin-moderate',
  imports: [DatePipe, ShortNumberPipe, TooltipDirective, InfiniteScrollDirective],
  templateUrl: './moderate.html',
  styleUrl: './moderate.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class Moderate implements OnDestroy {

  private static readonly PAGE_SIZE = 25;

  protected readonly CreatorModerationStatusEnum = CreatorModerationStatusEnum;

  readonly scrollDistance = 2;
  readonly scrollThrottle = 100;

  selectedTab = signal<CreatorModerationStatusEnum>(CreatorModerationStatusEnum.All);
  selectedFilter = signal<CreatorModerationStatusEnum>(CreatorModerationStatusEnum.All);

  isLoading = signal(false);
  isLoadingMore = signal(false);
  hasMore = signal(true);

  pageNumber = signal(0);
  readonly searchQuery = signal('');
  filteredCreators = signal<CreatorResponseDto[] | undefined>(undefined);
  creatorsData = signal<CreatorsFilterResponse | undefined>(undefined);
  totalCount = signal(0);
  filtersApplied = signal<FilterCreatorsDto>({
    limitModerationStatus: [-1],
  });

  overallFilterStats = signal<ModerationFilterStatsDto | null>(null);
  statusFilterButtons = signal<CreatorStatusFilterButton[]>([]);

  private subscriptionList = new Subscription();
  private readonly searchTrigger = new Subject<string>();
  private listGeneration = 0;
  private creatorsRequest: Subscription | null = null;

  selectedForVerification = signal<string | null>(null);
  isVerifying = signal(false);

  selectedForReady = signal<string | null>(null);
  isMarkingAsReady = signal(false);

  /** Services */
  private adminFilterService = inject(AdminFilter);
  private creatorService = inject(CreatorService);
  private moderateService = inject(ModerateService);
  readonly imageService = inject(ImageService);
  readonly formatService = inject(FormatService);
  readonly toastNotificationService = inject(ToastNotificationService);
  /** Services - END */

  constructor() {
    effect(() => {
      // Only track admin filter changes — do not read pageNumber, filteredCreators,
      // or searchQuery here, or scroll pagination and debounced search will reset.
      const sourceFilters = this.adminFilterService.filtersObjectSignal();
      console.log('MOD effect(): ', sourceFilters);

      const filters: FilterCreatorsDto = { ...sourceFilters };
      if (
        filters.limitModerationStatus?.length &&
        this.arraysEqual(filters.limitModerationStatus, [-1])
      ) {
        console.log('😎😎😎 Moderate creators - effect - filtersApplied 😎😎😎 ', filters.limitModerationStatus);
        delete filters.limitModerationStatus;
      }

      this.pageNumber.set(0);
      this.filteredCreators.set(undefined);
      this.hasMore.set(true);
      this.isLoadingMore.set(false);
      this.filtersApplied.set(filters);

      this.getCreators(filters, 0, { showLoading: true });
      this.getModerationFilters();
    });

    this.subscriptionList.add(
      this.searchTrigger.pipe(debounceTime(300), distinctUntilChanged()).subscribe(() => {
        this.pageNumber.set(0);
        this.filteredCreators.set(undefined);
        this.hasMore.set(true);
        this.isLoadingMore.set(false);
        this.getCreators(this.filtersApplied(), 0, { showLoading: true });
      }),
    );
  }

  ngOnDestroy(): void {
    this.subscriptionList.unsubscribe();
  }

  onSearchInput(event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    this.searchQuery.set(value);
    this.searchTrigger.next(value.trim());
  }

  onScrollDown(): void {
    if (this.isLoading() || this.isLoadingMore() || !this.hasMore()) {
      return;
    }

    const nextPage = this.pageNumber() + 1;
    this.pageNumber.set(nextPage);
    this.getCreators(this.filtersApplied(), nextPage, { append: true });
  }

  getCreators(
    filters: FilterCreatorsDto,
    page: number,
    options?: { showLoading?: boolean; append?: boolean },
  ): void {
    const showLoading = options?.showLoading ?? false;
    const append = options?.append ?? false;
    const generation = ++this.listGeneration;

    if (append) {
      this.isLoadingMore.set(true);
    } else if (showLoading) {
      this.isLoading.set(true);
    }

    const term = untracked(() => this.searchQuery()).trim();
    const payload: FilterCreatorsDto = {
      ...filters,
      skip: page * Moderate.PAGE_SIZE,
      take: Moderate.PAGE_SIZE,
      sortField: 'creator.createdDate',
      sortOrder: 'DESC',
    };
    if (term) {
      payload.searchTerm = term;
    } else {
      delete payload.searchTerm;
    }

    console.log('filters: ', payload);

    this.creatorsRequest?.unsubscribe();
    const sub = (
      this.creatorService.inviteContentCreatorsFilterV2(payload) as Observable<CreatorsFilterResponse>
    )
      .pipe(
        catchError((err: unknown) => {
          console.error('Error fetching creators:', err);
          if (generation === this.listGeneration && append) {
            this.pageNumber.update((current) => Math.max(0, current - 1));
            this.hasMore.set(false);
          }
          return of({ count: append ? this.totalCount() : 0, data: [] as CreatorResponseDto[] });
        }),
        finalize(() => {
          if (generation !== this.listGeneration) {
            return;
          }
          this.isLoading.set(false);
          this.isLoadingMore.set(false);
        }),
      )
      .subscribe({
        next: (creators) => {
          if (generation !== this.listGeneration) {
            return;
          }
          console.log('creators: ', creators);
          this.creatorsData.set(creators);

          if (append) {
            const existing = this.filteredCreators() ?? [];
            const newCreators = creators.data.filter(
              (creator) =>
                !existing.some((x) => x.blazeFirebaseUid === creator.blazeFirebaseUid),
            );
            this.filteredCreators.set([...existing, ...newCreators]);
          } else {
            this.filteredCreators.set(creators.data);
          }

          this.totalCount.set(creators.count);
          this.hasMore.set((this.filteredCreators()?.length ?? 0) < creators.count);
        },
      });

    this.creatorsRequest = sub;
    this.subscriptionList.add(sub);
  }

  getModerationFilters() {
    const subModerationFilters = this.moderateService.getModerateFilterCountsV2().subscribe({
      next: response => {
        console.log('Filter Response: ', response);
        if (response) {
          this.overallFilterStats.set(response);
          this.statusFilterButtons.set(this.configureFilterButtons(response));
        }
      },
      error: err => {
        console.error('filterCountsErr: ', err);
      }
    });
    this.subscriptionList.add(subModerationFilters);
  }

  configureFilterButtons(filterStats: ModerationFilterStatsDto): CreatorStatusFilterButton[] {
    return [
      {
        moderationStatus: CreatorModerationStatusEnum.All,
        btnText: 'All',
        filterStats: filterStats.All ?? 0
      },
      {
        moderationStatus: CreatorModerationStatusEnum.Unreviewed,
        btnText: 'Un-Moderated',
        filterStats: filterStats.Unreviewed ?? 0
      },
      {
        moderationStatus: CreatorModerationStatusEnum.InReview,
        btnText: 'In-Review',
        filterStats: filterStats.InReview ?? 0
      },
      {
        moderationStatus: CreatorModerationStatusEnum.NeedMoreInfo,
        btnText: 'Almost',
        filterStats: filterStats.NeedMoreInfo ?? 0
      },
      {
        moderationStatus: CreatorModerationStatusEnum.needHumanApproval,
        btnText: 'Human',
        filterStats: filterStats.needHumanApproval ?? 0
      },
      {
        moderationStatus: CreatorModerationStatusEnum.readyToStart,
        btnText: 'Ready',
        filterStats: filterStats.readyToStart ?? 0
      },
      {
        moderationStatus: CreatorModerationStatusEnum.rejected,
        btnText: 'Rejected',
        filterStats: filterStats.rejected ?? 0
      },
    ];
  }

  updateSelectedFilter(filter: CreatorModerationStatusEnum) {
    console.log('updateSelectedFilter(): ', filter);
    this.selectedTab.set(filter);
    this.selectedFilter.set(filter);

    this.adminFilterService.filtersObjectSignal.set({
      ...this.adminFilterService.filtersObjectSignal(),
      limitModerationStatus: [filter],
    });
  }

  verifyCreator(blazeFirebaseUid: string) {
    this.selectedForVerification.set(blazeFirebaseUid);
    this.isVerifying.set(true);

    const updateBody = {
      isVerified: true
    }

    const subUpdateCreator = this.creatorService.updateCreatorPartial(blazeFirebaseUid, updateBody).subscribe({
      next: (response: CreatorResponseDto) => {
        if (response) {
          console.log('Creator Verified: ', response);
          this.isVerifying.set(false);
          this.selectedForVerification.set(null);

          this.toastNotificationService.alert({
            title: 'Creator Verified!',
            message: 'The creator has been verified successfully.',
            type: 'success',
            icon: 'success'
          });

          /**
           * 1. Find the creator in the filteredCreators array
           * 2. Update the creator in the filteredCreators array by setting the isVerified flag to true
           */

          const creator = this.filteredCreators()?.find(c => c.blazeFirebaseUid === blazeFirebaseUid);
          if (creator) {
            creator.isVerified = true;
          }
        }
      },
      error: error => {
        this.isVerifying.set(false);
        this.selectedForVerification.set(null);
        console.error(`Verification of ${blazeFirebaseUid} failed: `, error);
        this.toastNotificationService.alert({
          title: 'Verification Failed!',
          message: `Verification of ${blazeFirebaseUid} failed!`,
          type: 'error',
          icon: 'error'
        });
      }
    });
    this.subscriptionList.add(subUpdateCreator);
  }

  markAsReady(creator: CreatorResponseDto) {
    this.selectedForReady.set(creator.blazeFirebaseUid);
    this.isMarkingAsReady.set(true);

    let newModeration: CreateManualModerationDto = {
      creatorId: creator.blazeFirebaseUid,
      status: CreatorModerationStatusEnum.readyToStart,
      profilePic: !!creator.photoURL,
      firstName: !!creator.firstName,
      lastName: !!creator.lastName,
      connectedAccounts: 0,
      avgViews: 0,
      daysBetweenPosts: 99,
      portfolioItems: creator.videos?.length ?? 0,
    }

    const subCreateManualModeration = this.moderateService.createManualModeration(newModeration).subscribe({
      next: (response: any) => {
        console.log('Manual Moderation Created: ', response);


        // const creatorToUpdate = this.filteredCreators()?.find(c => c.blazeFirebaseUid === creator.blazeFirebaseUid);
        // if (creatorToUpdate) {
        //   creatorToUpdate['moderationStatus'] = CreatorModerationStatusEnum.readyToStart;
        //   this.filteredCreators.set([...(this.filteredCreators() ?? []), creatorToUpdate]);
        // }

        const creatorToUpdate: CreatorResponseDto = this.filteredCreators()?.find(c => c.blazeFirebaseUid === creator.blazeFirebaseUid) as CreatorResponseDto;
        if (creatorToUpdate) {
          creatorToUpdate.moderationStaus = CreatorModerationStatusEnum.readyToStart;
        }

        this.isMarkingAsReady.set(false);
        this.selectedForReady.set(null);

        this.toastNotificationService.alert({
          title: 'Marking as Ready Success!',
          message: `Marking as Ready for ${creator.blazeFirebaseUid} successful!`,
          type: 'success',
          icon: 'success'
        });
      },
      error: error => {
        this.isMarkingAsReady.set(false);
        this.selectedForReady.set(null);
        console.error('Marking as Ready failed: ', error);
        this.toastNotificationService.alert({
          title: 'Marking as Ready Failed!',
          message: `Marking as Ready for ${creator.blazeFirebaseUid} failed!`,
          type: 'error',
          icon: 'error'
        });
      }
    });
  }

  getTabClass(tab: CreatorModerationStatusEnum) {
    return this.selectedTab() === tab
      ? 'bg-gray-200 text-gray-900 dark:bg-white/10 dark:text-gray-200'
      : 'text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200';
  }

  getStatusName(status: CreatorModerationStatusEnum | null | undefined): string {
    switch (status) {
      case CreatorModerationStatusEnum.All:
        return 'All';
      case CreatorModerationStatusEnum.Unreviewed:
        return 'Un-Moderated';
      case CreatorModerationStatusEnum.InReview:
        return 'In-Review';
      case CreatorModerationStatusEnum.NeedMoreInfo:
        return 'Almost';
      case CreatorModerationStatusEnum.needHumanApproval:
        return 'Human';
      case CreatorModerationStatusEnum.readyToStart:
        return 'Ready';
      case CreatorModerationStatusEnum.rejected:
        return 'Rejected';
      default:
        return 'Unknown';
    }
  }

  getStatusStyle(status: CreatorModerationStatusEnum | null | undefined): string {
    switch (status) {
      case CreatorModerationStatusEnum.InReview:
        return 'bg-pink-50 text-pink-700 ring-pink-600/20';
      case CreatorModerationStatusEnum.NeedMoreInfo:
        return 'bg-orange-50 text-orange-700 ring-orange-600/20';
      case CreatorModerationStatusEnum.needHumanApproval:
        return 'bg-sky-50 text-sky-700 ring-sky-600/20';
      case CreatorModerationStatusEnum.readyToStart:
        return 'bg-green-50 text-green-700 ring-green-600/20';
      case CreatorModerationStatusEnum.rejected:
        return 'bg-red-50 text-red-700 ring-red-600/20';
      case CreatorModerationStatusEnum.Unreviewed:
      case CreatorModerationStatusEnum.All:
      default:
        return 'bg-slate-50 text-slate-700 ring-slate-600/20';
    }
  }

  arraysEqual(a: any[], b: any[]): boolean {
    return a.length === b.length && a.every((val, index) => val === b[index]);
  }
}
